// @ts-nocheck
import { useState, useEffect, memo, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Frown, Meh, Smile, SmilePlus, Video, Trash2, Clock, User, X, Phone, Copy, Check, History, Building, Mail, Star, ExternalLink, MessageCircle, PhoneCall, AlertTriangle, Ban, ShieldAlert } from 'lucide-react'
import { useCallHistory } from '../hooks/useCallHistory'
import { useCallFormatters } from '../hooks/useCallFormatters'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { useWebSocket } from '../hooks/useWebSocket'
import { OutboundCallDialog } from '../components/OutboundCallDialog'
import { isAnonymousNumber } from '../utils/phoneNumberHelper'
import { checkBlockStatus, blockFromCallHistory, unblockNumber } from '../utils/blocklistHelper'

/**
 * 過去の通話一覧アイテム（メモ化）
 */
const PastCallItem = memo(function PastCallItem({ call, formatDateTime, formatDuration, getCallTypeLabel, renderStatusBadge, onNavigate }) {
  const handleClick = useCallback(() => {
    onNavigate(call.callHistoryId)
  }, [call.callHistoryId, onNavigate])

  // メモセグメントがある場合は最新のものを表示
  const latestSegment = call.noteSegments && call.noteSegments.length > 0
    ? call.noteSegments[0]
    : null

  return (
    <div
      className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-all hover:shadow-sm group"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-600">{formatDateTime(call.startTime)}</span>
          <span className="text-gray-700 font-medium">{formatDuration(call.duration)}</span>
          {renderStatusBadge(call.status)}
        </div>
        <div className="flex items-center gap-2">
          {call.callType && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {getCallTypeLabel(call.callType)}
            </span>
          )}
          <ExternalLink className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      {call.handledByName && (
        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
          <User className="w-3 h-3" />
          {call.handledByName}
        </div>
      )}
      
      {/* タグ表示（通話レベルのタグ） */}
      {call.tags && call.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {call.tags.map((tag, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      
      {/* コメント・メモ表示 */}
      {(latestSegment || call.summary || call.notes) && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          {/* メモセグメントがある場合 */}
          {latestSegment && (
            <>
              {latestSegment.summary && (
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {latestSegment.summary}
                </div>
              )}
              {latestSegment.note && (
                <div className="text-sm text-gray-600 line-clamp-2">
                  {latestSegment.note}
                </div>
              )}
              {latestSegment.tags && latestSegment.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {latestSegment.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {call.noteSegments && call.noteSegments.length > 1 && (
                <div className="text-xs text-gray-400 mt-1">
                  +{call.noteSegments.length - 1}件のメモ
                </div>
              )}
            </>
          )}
          
          {/* メモセグメントがない場合は通話レベルの要約・メモを表示 */}
          {!latestSegment && (call.summary || call.notes) && (
            <>
              {call.summary && (
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {call.summary}
                </div>
              )}
              {call.notes && (
                <div className="text-sm text-gray-600 line-clamp-2">
                  {call.notes}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
})

/**
 * 過去の通話一覧（メモ化）
 */
const PastCallsList = memo(function PastCallsList({ pastCalls, loading, formatDateTime, formatDuration, getCallTypeLabel, renderStatusBadge, onNavigate }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (pastCalls.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
        過去の通話履歴がありません
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {pastCalls.map((pastCall) => (
        <PastCallItem
          key={pastCall.callHistoryId}
          call={pastCall}
          formatDateTime={formatDateTime}
          formatDuration={formatDuration}
          getCallTypeLabel={getCallTypeLabel}
          renderStatusBadge={renderStatusBadge}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  )
})

/**
 * メモセグメント一覧アイテム（メモ化）
 */
const NoteSegmentItem = memo(function NoteSegmentItem({ segment, isOwn, formatDateTime, onDelete }) {
  const handleDelete = useCallback(() => {
    onDelete(segment.noteSegmentId)
  }, [segment.noteSegmentId, onDelete])

  return (
    <div
      className={`p-4 rounded-lg border transition-colors ${
        isOwn
          ? 'border-blue-200 bg-blue-50/50 hover:bg-blue-50'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      {/* セグメントヘッダー */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5 font-medium text-gray-700">
            <User className="w-3.5 h-3.5" />
            {segment.createdByName || '不明'}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatDateTime(segment.clientCreatedAt || segment.serverCreatedAt)}
          </span>
        </div>

        {isOwn && (
          <button
            onClick={handleDelete}
            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-red-50"
            title="削除"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 要約 */}
      {segment.summary && (
        <p className="text-sm font-medium text-gray-900 mb-2">{segment.summary}</p>
      )}

      {/* メモ本文 */}
      {segment.note && (
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{segment.note}</p>
      )}
    </div>
  )
})

/**
 * メモセグメント一覧（メモ化）
 */
const NoteSegmentsList = memo(function NoteSegmentsList({ noteSegments, loading, currentUserId, formatDateTime, onDelete }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (noteSegments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
        メモがありません
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {noteSegments.map((segment) => (
        <NoteSegmentItem
          key={segment.noteSegmentId}
          segment={segment}
          isOwn={segment.createdBy === currentUserId}
          formatDateTime={formatDateTime}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
})

export default function CallHistoryDetailPage() {
  const { callHistoryId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const api = useApi()

  const [call, setCall] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [noteSegments, setNoteSegments] = useState([])
  const [loadingSegments, setLoadingSegments] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [pastCalls, setPastCalls] = useState([])
  const [loadingPastCalls, setLoadingPastCalls] = useState(false)
  const [customer, setCustomer] = useState(null)
  const [loadingCustomer, setLoadingCustomer] = useState(false)

  // 折り返し発信ダイアログ
  const [isCallbackDialogOpen, setIsCallbackDialogOpen] = useState(false)
  
  // 削除確認ダイアログ
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  
  // localhost環境かどうかを判定
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  // メタデータ（callType, satisfaction, tags）用
  const [formData, setFormData] = useState({
    callType: '',
    satisfaction: '',
    tags: []
  })

  // 新規メモ入力用
  const [newNote, setNewNote] = useState({
    note: '',
    summary: ''
  })
  
  // タグ入力用（通話履歴レベルのタグ追加で使用）
  const [newTag, setNewTag] = useState('')
  
  // 電話番号オプションから取得したクイックタグと通話種別
  const [quickTags, setQuickTags] = useState([])
  const [callTypeOptions, setCallTypeOptions] = useState([])

  // ブロック機能のステート
  const [blockStatus, setBlockStatus] = useState(null)
  const [isBlockLoading, setIsBlockLoading] = useState(false)
  const [showBlockConfirm, setShowBlockConfirm] = useState(false)
  const [blockReason, setBlockReason] = useState('迷惑電話')

  const { fetchCallHistoryById, fetchNoteSegments, createNoteSegment, deleteNoteSegment, updateCallHistory } = useCallHistory()
  const { formatDuration, formatDateTime, formatPhoneNumber, getCallTypeLabel, renderStatusBadge } = useCallFormatters()
  const currentUser = useAuthStore((state) => state.user)

  // WebSocket: CallHistory更新イベントを受信
  const handleWsMessage = useCallback((message) => {
    if (message.eventType === 'call-history.updated') {
      const { callHistoryId: updatedId, ...updateData } = message.data || {}

      // 現在表示中の通話履歴が更新された場合
      if (updatedId === callHistoryId) {
        console.log('[CallHistoryDetailPage] WebSocketで更新を受信:', updateData)

        setCall((prevCall) => {
          if (!prevCall) return prevCall
          return { ...prevCall, ...updateData }
        })

        if (updateData.tags !== undefined) {
          setFormData((prev) => ({ ...prev, tags: updateData.tags }))
        }
        if (updateData.callType !== undefined) {
          setFormData((prev) => ({ ...prev, callType: updateData.callType }))
        }
        if (updateData.satisfaction !== undefined) {
          setFormData((prev) => ({ ...prev, satisfaction: updateData.satisfaction }))
        }
      }
    }
  }, [callHistoryId])

  useWebSocket(handleWsMessage)

  // デフォルトのクイックタグと通話種別
  const defaultQuickTags = ['製品A', '製品B', '使い方', 'トラブル', 'フォローアップ', '見積', '契約']
  const defaultCallTypeOptions = [
    { value: 'inquiry', label: '問い合わせ' },
    { value: 'complaint', label: 'クレーム' },
    { value: 'order', label: '注文' },
    { value: 'support', label: 'サポート' },
    { value: 'other', label: 'その他' },
  ]

  // ページ読み込み時にスクロールをトップに戻す
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [callHistoryId])

  // 通話履歴データ読み込み
  useEffect(() => {
    loadCallHistory()
  }, [callHistoryId])

  // ブロック状態をチェック
  useEffect(() => {
    const checkBlock = async () => {
      if (!call) return
      
      const phoneNumber = call.originalDirection === 'inbound' ? call.from : call.to
      if (!phoneNumber || isAnonymousNumber(phoneNumber)) {
        setBlockStatus(null)
        return
      }

      try {
        const status = await checkBlockStatus(phoneNumber)
        setBlockStatus(status)
      } catch (err) {
        console.error('ブロック状態チェックエラー:', err)
        setBlockStatus(null)
      }
    }

    checkBlock()
  }, [call])

  // ブロック処理
  const handleBlock = async () => {
    if (!call) return
    
    const phoneNumber = call.originalDirection === 'inbound' ? call.from : call.to
    if (!phoneNumber || isAnonymousNumber(phoneNumber)) {
      alert('電話番号が不明なためブロックできません')
      return
    }

    setIsBlockLoading(true)
    try {
      // 最新のメモセグメントを取得してブロック理由に含める
      const latestNote = noteSegments.length > 0 ? noteSegments[0].note : ''
      await blockFromCallHistory(call, latestNote, blockReason)
      
      // ブロック状態を更新
      const status = await checkBlockStatus(phoneNumber)
      setBlockStatus(status)
      setShowBlockConfirm(false)
      setBlockReason('迷惑電話')
      
      alert('この番号をブロックリストに追加しました')
    } catch (err) {
      console.error('ブロックエラー:', err)
      alert('ブロックに失敗しました: ' + err.message)
    } finally {
      setIsBlockLoading(false)
    }
  }

  // ブロック解除処理
  const handleUnblock = async () => {
    if (!blockStatus?.blockInfo?.blockId) {
      return
    }

    if (!confirm('この番号のブロックを解除しますか？')) {
      return
    }

    setIsBlockLoading(true)
    try {
      await unblockNumber(blockStatus.blockInfo.blockId)
      
      // ブロック状態を更新
      const phoneNumber = call.originalDirection === 'inbound' ? call.from : call.to
      const status = await checkBlockStatus(phoneNumber)
      setBlockStatus(status)
      
      alert('ブロックを解除しました')
    } catch (err) {
      console.error('ブロック解除エラー:', err)
      alert('ブロック解除に失敗しました: ' + err.message)
    } finally {
      setIsBlockLoading(false)
    }
  }

  const loadCallHistory = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchCallHistoryById(callHistoryId)
      setCall(data)
      setFormData({
        callType: data.callType || '',
        satisfaction: data.satisfaction || '',
        tags: data.tags || []
      })
      
      // 電話番号オプションからクイックタグを取得
      await loadPhoneNumberOptions(data)
      // noteSegmentsがレスポンスに含まれている場合はそれを使用
      if (data.noteSegments) {
        setNoteSegments(data.noteSegments)
      } else {
        await loadNoteSegments()
      }
      // 同じ電話番号の過去の通話を取得
      // 通話相手の電話番号を取得（着信時はfrom、発信時はto）
      const customerPhone = data.originalDirection === 'inbound'
        ? (data.from || data.phoneNumber)
        : data.to
      if (customerPhone) {
        loadPastCalls(customerPhone, data.callHistoryId)
      }
      // 顧客情報を取得（customerIdがあれば直接取得、なければ電話番号で検索）
      if (data.customerId) {
        loadCustomerById(data.customerId)
      } else if (customerPhone) {
        loadCustomerByPhone(customerPhone)
      }
    } catch (err) {
      console.error('Failed to load call history:', err)
      setError('通話履歴の読み込みに失敗しました')
    } finally {
      setLoading(false)
    }
  }
  
  // 電話番号オプションを取得（クイックタグと通話種別用）
  const loadPhoneNumberOptions = async (callData) => {
    try {
      // 利用した自社の電話番号（着信時はto、発信時はfrom）
      const usedNumber = callData.originalDirection === 'inbound' ? callData.to : callData.from
      if (!usedNumber) {
        // 電話番号がない場合はデフォルトを使用
        setQuickTags(defaultQuickTags)
        setCallTypeOptions(defaultCallTypeOptions)
        return
      }
      
      // 組織の電話番号リストを取得
      const phoneData = await api.get('/api/phone-numbers')
      // 通話で使用した電話番号に一致するものを検索
      const matchedPhone = phoneData.items?.find(p => p.phoneNumber === usedNumber)
      
      if (matchedPhone) {
        // クイックタグの設定
        if (matchedPhone.quickTags && matchedPhone.quickTags.length > 0) {
          // 電話番号に設定されたクイックタグを使用（labelのみを配列に）
          setQuickTags(matchedPhone.quickTags.map(tag => tag.label))
        } else {
          // 設定がない場合はデフォルトを使用
          setQuickTags(defaultQuickTags)
        }
        
        // 通話種別オプションの設定
        if (matchedPhone.callTypeOptions && matchedPhone.callTypeOptions.length > 0) {
          // 電話番号に設定された通話種別オプションを使用
          setCallTypeOptions(matchedPhone.callTypeOptions)
        } else {
          // 設定がない場合はデフォルトを使用
          setCallTypeOptions(defaultCallTypeOptions)
        }
      } else {
        // 電話番号が見つからない場合はデフォルトを使用
        setQuickTags(defaultQuickTags)
        setCallTypeOptions(defaultCallTypeOptions)
      }
    } catch (err) {
      console.error('電話番号オプション取得エラー:', err)
      // エラー時はデフォルトを使用
      setQuickTags(defaultQuickTags)
      setCallTypeOptions(defaultCallTypeOptions)
    }
  }

  // 顧客情報をIDで取得
  const loadCustomerById = async (customerId) => {
    try {
      setLoadingCustomer(true)
      const data = await api.get(`/api/customers/${customerId}`)
      if (data) {
        setCustomer(data)
      } else {
        setCustomer(null)
      }
    } catch (err) {
      console.error('Failed to load customer by id:', err)
      setCustomer(null)
    } finally {
      setLoadingCustomer(false)
    }
  }

  // 顧客情報を電話番号で検索
  const loadCustomerByPhone = async (phoneNumber) => {
    try {
      setLoadingCustomer(true)
      const data = await api.get(`/api/customers?search=${encodeURIComponent(phoneNumber)}&limit=1`)
      if (data?.customers?.length > 0) {
        setCustomer(data.customers[0])
      } else {
        setCustomer(null)
      }
    } catch (err) {
      console.error('Failed to load customer by phone:', err)
    } finally {
      setLoadingCustomer(false)
    }
  }

  // 同じ電話番号の過去の通話を取得
  const loadPastCalls = async (phoneNumber, currentCallHistoryId) => {
    try {
      setLoadingPastCalls(true)
      const params = new URLSearchParams()
      params.append('phoneNumber', phoneNumber)
      params.append('limit', '10')
      params.append('sortBy', 'startTime')
      params.append('sortOrder', 'desc')

      const data = await api.get(`/api/call-history?${params.toString()}`)
      // 現在の通話を除外
      const filteredCalls = (data.callHistory || []).filter(
        c => c.callHistoryId !== currentCallHistoryId
      )
      setPastCalls(filteredCalls)
    } catch (err) {
      console.error('Failed to load past calls:', err)
    } finally {
      setLoadingPastCalls(false)
    }
  }

  // NoteSegments読み込み
  const loadNoteSegments = async () => {
    if (!callHistoryId) return

    try {
      setLoadingSegments(true)
      const segments = await fetchNoteSegments(callHistoryId)
      setNoteSegments(segments)
    } catch (err) {
      console.error('Failed to load note segments:', err)
    } finally {
      setLoadingSegments(false)
    }
  }

  // 戻るボタン
  const handleBack = () => {
    // location.stateにフィルター情報があれば使用
    const searchParams = location.state?.searchParams
    if (searchParams) {
      navigate(`/call-history?${searchParams}`)
    } else {
      navigate('/call-history')
    }
  }

  // URLコピー
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy URL:', err)
    }
  }

  // メタデータ変更
  const handleMetaChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // 新規メモ変更
  const handleNewNoteChange = (e) => {
    const { name, value } = e.target
    setNewNote(prev => ({ ...prev, [name]: value }))
  }

  // 通話履歴レベルのタグ追加
  const handleAddCallHistoryTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }))
      setNewTag('')
    }
  }

  // 通話履歴レベルのタグ削除
  const handleRemoveCallHistoryTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  // メモセグメント追加
  const handleAddNoteSegment = async () => {
    // note、summaryのいずれかが必要
    if (!newNote.note.trim() && !newNote.summary.trim()) return

    try {
      setSaving(true)
      setError(null)

      await createNoteSegment(callHistoryId, {
        note: newNote.note,
        summary: newNote.summary,
        callType: formData.callType
      })

      // 入力をクリア
      setNewNote({ note: '', summary: '' })

      // リストを更新
      await loadNoteSegments()
    } catch (err) {
      console.error('Failed to add note segment:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // メモセグメント削除（メモ化）
  const handleDeleteSegment = useCallback(async (segmentId) => {
    if (!confirm('このメモを削除しますか？')) return

    try {
      setSaving(true)
      await deleteNoteSegment(callHistoryId, segmentId)
      await loadNoteSegments()
    } catch (err) {
      console.error('Failed to delete note segment:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }, [callHistoryId, deleteNoteSegment])

  // 過去通話への遷移（メモ化）
  const handleNavigateToPastCall = useCallback((pastCallHistoryId) => {
    navigate(`/call-history/${pastCallHistoryId}`)
  }, [navigate])

  // メタデータ保存
  const handleSaveMetadata = async () => {
    try {
      setSaving(true)
      setError(null)

      await updateCallHistory(callHistoryId, {
        callType: formData.callType,
        satisfaction: formData.satisfaction,
        tags: formData.tags
      })

      // 通話履歴を再読み込み
      await loadCallHistory()
    } catch (err) {
      console.error('Failed to save metadata:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }
  
  // 通話履歴を削除
  const handleDelete = async () => {
    try {
      setDeleting(true)
      setError(null)
      
      await api.delete(`/api/call-history/${callHistoryId}`)
      
      // 削除成功後、一覧画面に戻る
      const searchParams = location.state?.searchParams
      if (searchParams) {
        navigate(`/call-history?${searchParams}`)
      } else {
        navigate('/call-history')
      }
    } catch (err) {
      console.error('Failed to delete call history:', err)
      setError(err.response?.data?.error || '通話履歴の削除に失敗しました')
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error && !call) {
    return (
      <div className="p-6">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            通話履歴に戻る
          </button>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  if (!call) {
    return (
      <div className="p-6">
        <div className="mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            通話履歴に戻る
          </button>
        </div>
        <div className="text-center py-8 text-gray-500">
          通話履歴が見つかりませんでした
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">通話履歴に戻る</span>
          </button>
          <div className="flex items-center gap-3">
            {/* 着信の場合のみ折り返しボタンを表示（非通知はdisabled） */}
            {call.originalDirection === 'inbound' && (
              <button
                onClick={() => {
                  if (!isAnonymousNumber(call.from) && !isAnonymousNumber(call.phoneNumber)) {
                    setIsCallbackDialogOpen(true)
                  }
                }}
                disabled={isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber)}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg shadow-sm transition-colors ${
                  isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'text-white bg-blue-600 hover:bg-blue-700'
                }`}
                title={isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber) ? '非通知のため折り返しできません' : '折り返し発信'}
              >
                <PhoneCall className="w-4 h-4" />
                折り返し発信
              </button>
            )}
            {/* localhost環境のみ削除ボタンを表示 */}
            {isLocalhost && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 bg-white border border-red-300 rounded-lg shadow-sm hover:bg-red-50 transition-colors"
                title="開発環境のみ"
              >
                <Trash2 className="w-4 h-4" />
                削除
              </button>
            )}
            <button
              onClick={handleCopyUrl}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">コピーしました</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  URLをコピー
                </>
              )}
            </button>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">通話詳細</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* メインコンテンツ 2カラムレイアウト */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左カラム: 通話情報・顧客情報 */}
        <div className="lg:col-span-1 space-y-6">
          {/* 通話情報カード */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
              <Phone className="w-4 h-4 text-blue-600" />
              通話情報
            </h2>
            <div className="space-y-4">
              {/* 通話相手: この画面の主役なので最上部に大きく表示する */}
              <div className="pb-3 border-b border-gray-100">
                <div className="text-xs text-gray-500 mb-1">通話相手</div>
                {(() => {
                  // 通話相手の電話番号を取得（着信時はfrom、発信時はto）
                  const customerPhone = call.originalDirection === 'inbound'
                    ? (call.from || call.phoneNumber)
                    : call.to

                  // customerNameが電話番号形式かどうかを判定
                  // 1. customerNameが存在しない場合は未登録
                  // 2. customerNameが電話番号と完全一致する場合は未登録
                  // 3. customerNameが+で始まり、10桁以上の数字を含む場合は電話番号として判定
                  const isPhoneNumberName = call.customerName && (
                    call.customerName === customerPhone ||
                    call.customerName === call.from ||
                    call.customerName === call.to ||
                    /^\+?\d{10,}$/.test(call.customerName.replace(/[\s\-()]/g, ''))
                  )
                  const hasValidName = call.customerName && !isPhoneNumberName

                  return hasValidName ? (
                    <>
                      <div className="text-xl font-bold text-gray-900 leading-tight">{call.customerName}</div>
                      <div className="text-sm text-gray-500 mt-1">{formatPhoneNumber(customerPhone)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-xl font-bold text-gray-900 leading-tight">{formatPhoneNumber(customerPhone)}</div>
                      <div className="text-xs text-gray-500 mt-1">未登録</div>
                    </>
                  )
                })()}
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">日時</div>
                <div className="text-sm font-medium text-gray-900">{formatDateTime(call.startTime, { format: 'full' })}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">通話時間</div>
                  <div className="text-sm font-medium text-gray-900">{formatDuration(call.duration)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">ステータス</div>
                  <div>{renderStatusBadge(call.status)}</div>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">担当オペレーター</div>
                <div className="text-sm font-medium text-gray-900">{call.handledByName || '未対応'}</div>
                {/* 複数の担当者がいる場合は詳細を表示 */}
                {call.operators && call.operators.length > 1 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-2">担当履歴</div>
                    <div className="space-y-1.5">
                      {call.operators.map((operator, index) => {
                        const roleLabels = {
                          'initial': '初回対応',
                          'resumed': '保留解除',
                          'transferred_from': '転送元',
                          'transferred_to': '転送先'
                        }
                        const roleLabel = roleLabels[operator.role] || operator.role
                        
                        // タイムスタンプをフォーマット（ミリ秒のUNIXタイムスタンプの場合）
                        let timeDisplay = '-'
                        if (operator.timestamp) {
                          try {
                            const date = new Date(operator.timestamp)
                            if (!isNaN(date.getTime())) {
                              timeDisplay = date.toLocaleTimeString('ja-JP', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            }
                          } catch (e) {
                            console.error('タイムスタンプ変換エラー:', e)
                          }
                        }
                        
                        return (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                              <span className="text-gray-700 font-medium">{operator.name || 'Unknown'}</span>
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                                {roleLabel}
                              </span>
                            </div>
                            <span className="text-gray-400">
                              {timeDisplay}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">利用番号</div>
                {(() => {
                  // 利用した自社の電話番号（着信時はto、発信時はfrom）
                  const usedNumber = call.originalDirection === 'inbound' ? call.to : call.from
                  // phoneNumberNameがあればそれを使用、なければ電話番号をフォーマット
                  const displayName = call.phoneNumberName || formatPhoneNumber(usedNumber)
                  return (
                    <div className="text-sm font-medium text-gray-900">{displayName}</div>
                  )
                })()}
              </div>
              {/* タグ表示 */}
              {call.tags && call.tags.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-2">タグ</div>
                  <div className="flex flex-wrap gap-1.5">
                    {call.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ブロック機能カード */}
          {!isAnonymousNumber(call.originalDirection === 'inbound' ? call.from : call.to) && (
            <div className="bg-white rounded-lg shadow p-5">
              {blockStatus?.isBlocked ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-red-900 mb-1">
                        ブロック済み
                      </h3>
                      <p className="text-xs text-red-700">
                        この番号はブロックリストに登録されています
                      </p>
                    </div>
                  </div>
                  {blockStatus.blockInfo && (
                    <div className="text-xs text-red-700 space-y-1.5 mb-3 pl-8">
                      <div>
                        <span className="font-medium">理由:</span> {blockStatus.blockInfo.reason}
                      </div>
                      {blockStatus.blockInfo.notes && (
                        <div>
                          <span className="font-medium">メモ:</span> {blockStatus.blockInfo.notes}
                        </div>
                      )}
                      {blockStatus.blockInfo.blockedBy && (
                        <div>
                          <span className="font-medium">登録者:</span> {blockStatus.blockInfo.blockedBy}
                        </div>
                      )}
                      {blockStatus.blockInfo.blockedAt && (
                        <div>
                          <span className="font-medium">登録日時:</span>{' '}
                          {formatDateTime(blockStatus.blockInfo.blockedAt)}
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleUnblock}
                    disabled={isBlockLoading}
                    className="w-full px-3 py-2 bg-white text-red-700 border border-red-300 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 transition"
                  >
                    {isBlockLoading ? 'ブロック解除中...' : 'ブロックを解除'}
                  </button>
                </div>
              ) : (
                <div>
                  {!showBlockConfirm ? (
                    <button
                      onClick={() => setShowBlockConfirm(true)}
                      disabled={isBlockLoading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition"
                    >
                      <Ban className="w-4 h-4" />
                      <span className="font-medium">この番号をブロック</span>
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-sm text-red-900 mb-3">
                        ブロック理由を選択
                      </h3>
                      <select
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="迷惑電話">迷惑電話</option>
                        <option value="営業電話">営業電話</option>
                        <option value="詐欺の疑い">詐欺の疑い</option>
                        <option value="クレーマー">クレーマー</option>
                        <option value="その他">その他</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={handleBlock}
                          disabled={isBlockLoading}
                          className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
                        >
                          {isBlockLoading ? 'ブロック中...' : 'ブロック実行'}
                        </button>
                        <button
                          onClick={() => {
                            setShowBlockConfirm(false)
                            setBlockReason('迷惑電話')
                          }}
                          disabled={isBlockLoading}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 disabled:opacity-50 transition"
                        >
                          キャンセル
                        </button>
                      </div>
                      {noteSegments.length > 0 && (
                        <p className="text-xs text-red-600 mt-2">
                          ※ 通話メモがブロック理由に自動的に含まれます
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 顧客情報カード */}
          {loadingCustomer ? (
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            </div>
          ) : customer ? (
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-green-600" />
                  顧客情報
                </h2>
                <button
                  onClick={() => navigate(`/customers/${customer.customerId}`)}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                >
                  詳細を見る
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{customer.name || '名前未登録'}</span>
                  {customer.priority === 'vip' && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full inline-flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      VIP
                    </span>
                  )}
                  {customer.priority === 'high' && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                      優先
                    </span>
                  )}
                </div>
                {customer.companyName && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building className="w-4 h-4 text-gray-400" />
                    {customer.companyName}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" />
                  {formatPhoneNumber(customer.phoneNumber)}
                </div>
                {customer.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {customer.email}
                  </div>
                )}
                {customer.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-600">{customer.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-500">
                  <User className="w-4 h-4" />
                  <span className="text-sm">顧客未登録</span>
                </div>
                <button
                  onClick={() => navigate(`/customers?newPhone=${encodeURIComponent(call.phoneNumber || call.from)}`)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  顧客として登録
                </button>
              </div>
            </div>
          )}

          {/* メタデータ編集カード（通話種別・満足度・タグ） */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100">通話分類・タグ</h2>
            <div className="space-y-4">
              {/* 通話種別 */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">
                  通話種別
                </label>
                <select
                  name="callType"
                  value={formData.callType}
                  onChange={handleMetaChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">未分類</option>
                  {callTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 満足度 */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">
                  顧客満足度
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'poor', label: '不満', icon: Frown, color: 'bg-red-50 text-red-700 hover:bg-red-100', activeColor: 'bg-red-100 text-red-800 ring-2 ring-red-500' },
                    { value: 'neutral', label: '普通', icon: Meh, color: 'bg-gray-50 text-gray-700 hover:bg-gray-100', activeColor: 'bg-gray-100 text-gray-800 ring-2 ring-gray-500' },
                    { value: 'good', label: '良好', icon: Smile, color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', activeColor: 'bg-blue-100 text-blue-800 ring-2 ring-blue-500' },
                    { value: 'excellent', label: '優秀', icon: SmilePlus, color: 'bg-green-50 text-green-700 hover:bg-green-100', activeColor: 'bg-green-100 text-green-800 ring-2 ring-green-500' }
                  ].map((option) => {
                    const Icon = option.icon
                    const isActive = formData.satisfaction === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, satisfaction: option.value }))}
                        className={`flex flex-col items-center justify-center py-3 rounded-lg text-xs font-medium transition-all ${
                          isActive ? option.activeColor : option.color
                        }`}
                        title={option.label}
                      >
                        <Icon className="w-5 h-5 mb-1" />
                        <span className="text-[10px]">{option.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* タグ */}
              <div>
                <label className="block text-xs text-gray-500 mb-2">
                  タグ
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCallHistoryTag())}
                    placeholder="新しいタグ"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddCallHistoryTag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                  >
                    追加
                  </button>
                </div>

                {/* クイックタグ */}
                <div className="mb-3">
                  <div className="text-xs text-gray-400 mb-2">よく使うタグ</div>
                  <div className="flex flex-wrap gap-2">
                    {quickTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          if (!formData.tags.includes(tag)) {
                            setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))
                          }
                        }}
                        disabled={formData.tags.includes(tag)}
                        className="px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveCallHistoryTag(tag)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleSaveMetadata}
                disabled={saving}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors"
              >
                {saving ? '保存中...' : '分類・タグを保存'}
              </button>
            </div>
          </div>
        </div>

        {/* 右カラム: 録音・メモ */}
        <div className="lg:col-span-2 space-y-6">
          {/* 録音プレーヤー */}
          {call.recordingUrl && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-5 border border-blue-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Video className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-sm font-semibold text-gray-900">録音データ</h2>
              </div>
              <audio
                controls
                className="w-full"
                preload="metadata"
              >
                <source src={call.recordingUrl} type="audio/mpeg" />
                <source src={call.recordingUrl} type="audio/wav" />
                お使いのブラウザは音声再生に対応していません。
              </audio>
            </div>
          )}

          {/* 通話メモ一覧 */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-purple-600" />
              通話メモ
              {noteSegments.length > 0 && (
                <span className="ml-auto px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {noteSegments.length}件
                </span>
              )}
            </h2>

            <NoteSegmentsList
              noteSegments={noteSegments}
              loading={loadingSegments}
              currentUserId={currentUser?.userId}
              formatDateTime={formatDateTime}
              onDelete={handleDeleteSegment}
            />
          </div>

          {/* 新規メモ入力 */}
          <div className="bg-white rounded-lg shadow p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
              <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-xs font-bold">+</span>
              新しいメモを追加
            </h2>

            {/* 要約 */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-2">
                要約
              </label>
              <input
                type="text"
                name="summary"
                value={newNote.summary}
                onChange={handleNewNoteChange}
                placeholder="通話の要約を簡潔に入力"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            {/* 詳細メモ */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-2">
                詳細メモ
              </label>
              <textarea
                name="note"
                value={newNote.note}
                onChange={handleNewNoteChange}
                rows={4}
                placeholder="通話の詳細、対応内容、フォローアップ事項など"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              />
            </div>

            <button
              onClick={handleAddNoteSegment}
              disabled={saving || (!newNote.note.trim() && !newNote.summary.trim())}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {saving ? '追加中...' : 'メモを追加'}
            </button>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                💡 タグは左カラムの「通話分類・タグ」から追加できます
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* この電話番号の過去の通話 */}
      <div className="mt-8">
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
            <History className="w-4 h-4 text-orange-600" />
            この電話番号の過去の通話
            {pastCalls.length > 0 && (
              <span className="ml-auto px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                {pastCalls.length}件
              </span>
            )}
          </h2>

          <PastCallsList
            pastCalls={pastCalls}
            loading={loadingPastCalls}
            formatDateTime={formatDateTime}
            formatDuration={formatDuration}
            getCallTypeLabel={getCallTypeLabel}
            renderStatusBadge={renderStatusBadge}
            onNavigate={handleNavigateToPastCall}
          />
        </div>
      </div>

      {/* 折り返し発信ダイアログ */}
      <OutboundCallDialog
        isOpen={isCallbackDialogOpen}
        onClose={() => setIsCallbackDialogOpen(false)}
        callbackCallHistory={call}
      />
      
      {/* 削除確認ダイアログ */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">通話履歴を削除</h3>
                <p className="text-sm text-gray-500">この操作は取り消せません</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              この通話履歴とすべての関連メモを削除してもよろしいですか？
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    削除中...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    削除する
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
